<template>
  <Main>
    <template v-slot:breadcrumb>
      <BaseBreadcrumb
        @return="previousPage"
        :items="breadcrumb"
        class="d-none d-sm-flex"
      />
    </template>
    <template v-slot:breadcrumb-mobile>
      <BaseBreadcrumb @return="previousPage" :items="breadcrumb" />
    </template>
    <UserForm
      v-if="showForm && !showWalletForm"
      :user="user"
      :userId="userId()"
      :profileOptions="profileOptions"
      :typeProfileOptions="typeProfileOptions"
      :structureOptions="structureOptions"
      :areaOptions="areaOptions"
      :economicGroupOptions="economicGroupOptions"
      :officeOptions="officeOptions"
      :approversList="approversList"
      @filter-profile="filterProfileOptions"
      @get-offices="getOfficeOptions"
      @submit-user="submit"
      @go-wallet="goToWallet"
    >
    </UserForm>
    <WalletGroupsForm
      v-if="showWalletForm"
      :groups="walletGroups"
      :userName="user.nome"
      :userProfile="user.perfil.nome"
      @close-wallet="showWalletForm = false"
      @transfer-wallet="transferWallet"
    >
    </WalletGroupsForm>
    <ModalCancelUser
      @close="closeModalCancel"
      v-model="modalCancel"
      title="SAIR SEM SALVAR"
      text="As alterações não foram salvas. Deseja realmente sair da tela?"
    />
  </Main>
</template>

<script>
const DEFAULT_OFFICE = {
  id: "00000000-0000-0000-0000-000000000000",
  nome_fantasia: "NENHUM"
};
import { mapActions } from "vuex";
import { LoadingRequest } from "@/mixins/LoadingMixin";
import { formatIsoDateToDDMMYYYY } from "@/utils/formats.js";
import UserForm from "@/modules/user/components/UserForm.vue";
import WalletGroupsForm from "@/modules/user/components/WalletGroupsForm.vue";
import _ from "lodash";
import ModalCancelUser from "../../insurance_company/components/ModalCancelInsurance";
export default {
  name: "UserDetails",
  beforeRouteLeave(to, from, next) {
    if (this.cancel || this.formIsPristine()) next();
    this.modalCancel = true;
    this.nextRoute = to;
  },
  mixins: [LoadingRequest],
  components: {
    UserForm,
    WalletGroupsForm,
    ModalCancelUser
  },
  data: () => ({
    user: {
      perfil: null
    },
    formatIsoDateToDDMMYYYY,
    profileOptions: [],
    allProfileOptions: [],
    structureOptions: [],
    areaOptions: [],
    economicGroupOptions: [],
    officeOptions: [DEFAULT_OFFICE],
    showForm: false,
    showWalletForm: false,
    walletGroups: [],
    typeProfileOptions: [],
    modalCancel: false,
    cancel: false,
    nextRoute: null,
    cloneUser: {},
    approversList: []
  }),
  computed: {
    breadcrumb() {
      let title = "";
      if (!this.showWalletForm) {
        title = this.userId() ? "Editar Usuário" : "Adicionar Novo Usuário";
      } else {
        title = "Carteira do usuário";
      }
      return [
        {
          text: title,
          to: ""
        }
      ];
    }
  },
  methods: {
    ...mapActions("user", [
      "requestUser",
      "editUser",
      "createUser",
      "requestEconomicGroupSuggestions",
      "requestUserWallet",
      "transferUserWallet",
      "requestOfficeSuggestions",
      "requestTypeProfiles",
      "requestAreaSuggestion"
    ]),
    ...mapActions("profile", [
      "requestProfileSuggestion",
      "requestStructureSuggestion"
    ]),
    ...mapActions(["showAlertNotification", "suggestUsers"]),
    formIsPristine() {
      // Obrigado mask pro facilitar minha vida ...
      return (
        (_.isEqual(
          _.omit(this.user, "telefone"),
          _.omit(this.cloneUser, "telefone")
        ) &&
          this.user.telefone &&
          this.user.telefone.replace(/\D/g, "") ===
            this.cloneUser.telefone.replace(/\D/g, "")) ||
        _.isEqual(
          _.omit(this.user, "telefone"),
          _.omit(this.cloneUser, "telefone")
        )
      );
    },
    async fetchData() {
      this.user = this.userId()
        ? await this.withLoading(this.requestUser({ userId: this.userId() }))
        : {};
      [
        "data_ultimo_acesso",
        "data_bloqueio",
        "data_reativacao",
        "criado_em"
      ].forEach(key => {
        this.user[key] =
          this.user[key] && formatIsoDateToDDMMYYYY(this.user[key]);
      });
      [
        this.structureOptions,
        this.areaOptions,
        this.allProfileOptions,
        this.typeProfileOptions
      ] = await this.withLoading(
        Promise.all([
          this.requestStructureSuggestion(),
          this.requestAreaSuggestion(),
          this.requestProfileSuggestion(),
          this.requestTypeProfiles()
        ])
      );
      if (!this.user.estrutura) {
        this.user.estrutura = this.structureOptions[0];
      }
      if (!this.user.area_usuario) {
        this.user.area_usuario = this.areaOptions[0];
      }
      this.profileOptions = this.allProfileOptions.filter(
        profile => profile.estrutura.id === this.user.estrutura.id
      );
      this.economicGroupOptions = await this.withLoading(
        this.requestEconomicGroupSuggestions("")
      );
      if (this.user.grupos_economicos) await this.getOfficeOptions();
      if (!this.user.escritorio) this.user.escritorio = this.officeOptions[0];
      this.cloneUser = _.cloneDeep(this.user);
      this.showForm = true;
    },
    userId() {
      const id = this.$route.params.userId;
      return id !== "novo-usuario" ? id : "";
    },
    submit() {
      let payload = {
        nome: this.user.nome,
        telefone: this.user.telefone,
        email: this.user.email,
        estrutura: {
          id: this.user.estrutura.id
        },
        perfil: {
          id: this.user.perfil.id
        },
        posicao: this.user.posicao,
        ativo: this.user.ativo,
        assessoria: this.user.assessoria,
        grupos_economicos: this.user.grupos_economicos || [],
        escritorio: this.user.escritorio,
        aprovadores: this.user.aprovadores,
        solicitantes: this.user.solicitantes,
        area: this.user.area_usuario
      };
      if (this.userId()) {
        this.withLoading(
          this.editUser({ userId: this.userId(), payload: payload }).then(
            () => {
              this.showAlertNotification({
                message: "Usuário editado com sucesso!",
                type: "success"
              });
              this.cancel = true;
              this.previousPage();
            }
          )
        );
      } else {
        this.withLoading(
          this.createUser(payload).then(() => {
            this.showAlertNotification({
              message: "Usuário criado com sucesso!",
              type: "success"
            });
            this.cancel = true;
            this.previousPage();
          })
        );
      }
    },
    previousPage() {
      if (this.showWalletForm) {
        this.showWalletForm = false;
      } else this.$router.push({ name: "user" });
    },
    filterProfileOptions(item) {
      if (this.user.perfil) this.user.perfil = null;
      this.profileOptions = this.allProfileOptions.filter(
        profile => profile.estrutura.id === item.estrutura.id
      );
    },
    async getOfficeOptions() {
      const payload = {
        grupo_economico_ids: this.user.grupos_economicos.map(e => e.id)
      };
      this.officeOptions = await this.withLoading(
        this.requestOfficeSuggestions(payload)
      );
      this.officeOptions.unshift(DEFAULT_OFFICE);
      if (this.user.perfil?.papel_principal?.id != "geral")
        this.fetchApproversList();
    },
    async goToWallet() {
      this.walletGroups = await this.withLoading(
        this.requestUserWallet({ userId: this.userId() })
      );
      this.showWalletForm = true;
    },
    transferWallet(payload) {
      this.withLoading(
        this.transferUserWallet({ userId: this.userId(), payload })
      );
      this.showWalletForm = false;
    },
    closeModalCancel(value) {
      this.cancel = value;
      this.modalCancel = false;
      if (value) {
        this.$router.push({
          name: this.nextRoute.name,
          params: this.nextRoute.params
        });
      }
    },
    fetchApproversList() {
      // Se o papel do usuário for solicitante, ele deverá ter a lista de aprovadores
      // caso ele seja aprovador, ele terá a lista de solicitantes
      const typeUser =
        this.user.perfil.papel_principal.id == "solicitante"
          ? "aprovador"
          : "solicitante";
      const ids = this.user.grupos_economicos.map(e => e.id);
      const payload = `?&papel_principal_id=${typeUser}&grupo_economico_ids=${ids}`;
      if (ids.length >= 1) {
        this.withLoading(
          this.suggestUsers(payload).then(users => {
            this.approversList = users;
          })
        );
      } else {
        this.approversList = [];
      }
    }
  },
  mounted() {
    this.fetchData();
  }
};
</script>
